import { useCallback, useEffect, useState } from "react";
import { applyAccent, applyTheme, prefersDark, type Theme } from "./theme";
import { useSignedIn } from "./session";
import { DashboardLink, SectionLabel } from "./components/chrome";
import { LetCraigDoIt, Wordmark } from "./components/Brand";

/* ═══════════════════════════════════════════════════════════════════════
   The case, as the practice sees it.

   The target from the strategy: understand a normal case in under sixty
   seconds without reading the raw transcript. So the order is what a
   professional needs in the order they need it — what needs their
   judgement first, the facts second, the evidence third, and the raw
   material last and collapsed.

   The transcript is available and deliberately buried. If it is ever the
   fastest way to understand a case, the summary above it has failed.
   ═══════════════════════════════════════════════════════════════════════ */

interface LineItem {
  sourceId: string;
  what: string;
  amount?: string;
  when?: string;
  hasEvidence?: boolean;
  detail?: string;
  needsCheck?: boolean;
}

/**
 * What one assembled module has to say for itself.
 *
 * The server has sent these all along — see `modules` in routes/agent.ts —
 * and nothing rendered them, so anything a module collected arrived and
 * was invisible. Availability was the one that made that obvious: crAIg
 * asks when suits, records it, and whoever books the van never saw it.
 */
interface ModuleView {
  id: string;
  /** The module's own one-liner. "Weekday mornings, not Tuesday". */
  summary: string | null;
  done: boolean;
  /** Its full handoff shape, for anything the summary can't carry. */
  detail: unknown;
}

/** A module id, as somebody acting on the case would say it. */
const LABELS: Record<string, string> = {
  scheduling: "When suits them",
  fileSlots: "Asked for",
  files: "Documents",
  signature: "Signed",
  secure: "Locked box",
  document: "Shown to them",
};

interface AgentView {
  tenant: { displayName: string; slug: string };
  pack: {
    displayName: string;
    version: string;
    reviewerNoun: string;
    checklists: { id: string; title: string; options: { id: string; label: string }[] }[];
    fields: { name: string; label: string }[];
    secureGroups: { id: string; title: string; fields: string[] }[];
  };
  case: {
    id: string;
    status: string;
    language: string;
    service: { id: string; label: string } | null;
    declined: { rule: string; reason: string; at: string } | null;
    escalation: { reason: string; urgency: "now" | "today" | "soon"; at: string } | null;
    packVersion: string;
    updatedAt: string;
    fields: Record<string, string>;
    checklists: Record<string, string[]>;
    items: Record<string, LineItem[]>;
    topicNotes: Record<string, string>;
    documents: { id: string; name: string; mime: string; sizeBytes: number }[];
    consent: {
      signedName: string;
      signedAt: string;
      statement: string;
      basis: string;
      additional: Record<string, boolean>;
      signatureImage: string;
    } | null;
    secureFilled: Record<string, boolean>;
  };
  attention: string[];
  checklist: string[];
  clarifications: {
    id: string;
    field: string | null;
    question: string;
    askedBy: string | null;
    status: "open" | "answered" | "withdrawn";
    answer: string | null;
    askedAt: string;
    answeredAt: string | null;
  }[];
  coverage: { asked: number; total: number; remaining: number };
  modules?: ModuleView[];
  cost: { usd: number; turns: number };
}

export function AgentCase({ caseId, token }: { caseId: string; token: string }) {
  const [view, setView] = useState<AgentView | null>(null);
  const [error, setError] = useState<{ message: string; expired?: boolean } | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [revealing, setRevealing] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcript, setTranscript] = useState<{ role: string; text: string; secureLabel?: string }[]>([]);
  const [theme] = useState<Theme>(() => (prefersDark() ? "dark" : "light"));

  /* This screen opens from a signed link in an email, and the person
     reading it is not always the account holder — a practice forwards a
     job to whoever is doing it. Offering that person a dashboard would
     put them on a sign-in form for an account they haven't got, so the
     button appears only for somebody who actually has one. */
  const signedIn = useSignedIn();

  useEffect(() => {
    applyTheme(theme);
    // The practice's own colour isn't loaded here — this is our surface, not
    // their client-facing one, so it wears crAIg's.
    applyAccent(undefined, theme);
  }, [theme]);

  const refresh = useCallback(() => {
    fetch(`/api/agent/case/${caseId}?k=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const body = await r.json();
        if (!r.ok) throw Object.assign(new Error(body.error), { expired: body.expired });
        return body as AgentView;
      })
      .then(setView)
      .catch((e: Error & { expired?: boolean }) =>
        setError({ message: e.message, ...(e.expired ? { expired: true } : {}) }),
      );
  }, [caseId, token]);

  useEffect(refresh, [refresh]);

  const reveal = useCallback(
    async (group: string, field: string) => {
      const key = `${group}.${field}`;
      setRevealing(key);
      try {
        const res = await fetch(`/api/agent/case/${caseId}/reveal?k=${encodeURIComponent(token)}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ group, field }),
        });
        const body = await res.json();
        setRevealed((v) => ({ ...v, [key]: res.ok ? body.value : `— ${body.error}` }));
      } finally {
        setRevealing(null);
      }
    },
    [caseId, token],
  );

  const loadTranscript = useCallback(async () => {
    setShowTranscript(true);
    if (transcript.length) return;
    const res = await fetch(`/api/agent/case/${caseId}/history?k=${encodeURIComponent(token)}`);
    if (res.ok) setTranscript((await res.json()).transcript);
  }, [caseId, token, transcript.length]);

  if (error) {
    return (
      <Centre>
        <div className="card px-5 py-4" style={{ maxWidth: 420 }}>
          <p style={{ fontWeight: 650 }}>{error.expired ? "This link has expired" : "This link isn't valid"}</p>
          <p style={{ fontSize: 13.5, color: "var(--ink-2)", marginTop: 6 }}>
            {error.expired
              ? "Links last 14 days. Ask for a fresh one and it'll open straight away."
              : error.message}
          </p>
        </div>
      </Centre>
    );
  }

  if (!view) return <Centre><span style={{ color: "var(--ink-3)" }}>Loading the case…</span></Centre>;

  const c = view.case;
  const name = c.fields.name ?? "(no name given)";
  const buckets = Object.entries(c.items).filter(([, v]) => v?.length);

  const optionLabel = (id: string) => {
    for (const l of view.pack.checklists) {
      const o = l.options.find((x) => x.id === id);
      if (o) return o.label;
    }
    return id;
  };

  return (
    <div className="min-h-screen">
      <header style={{ background: "var(--surface)", borderBottom: "1px solid var(--line)" }}>
        <div className="max-w-4xl mx-auto px-5 py-3 flex items-center gap-3">
          {signedIn && <DashboardLink />}
          <Wordmark size={14} muted />
          <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
            {view.tenant.displayName}
          </span>
          <span className="ml-auto" style={{ fontSize: 12, color: "var(--ink-3)" }}>
            {view.pack.displayName}
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-6 space-y-4">
        <div className="card px-5 py-4">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 style={{ fontSize: 22, fontWeight: 650, letterSpacing: "-0.02em", margin: 0 }}>
              {name}
            </h1>
            <Pill tone={c.status === "awaiting_client" ? "warn" : c.consent ? "ok" : "warn"}>
              {c.status === "awaiting_client"
                ? "waiting on the client"
                : c.consent
                  ? c.status === "signed"
                    ? "signed"
                    : c.status
                  : c.status}
            </Pill>
            {c.service && <Pill>{c.service.label}</Pill>}
            {c.declined && <Pill tone="warn">declined</Pill>}
            {c.language !== "en" && <Pill>answered in {c.language}</Pill>}
          </div>
          <p style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 6 }}>
            {c.consent
              ? `Declared and signed by ${c.consent.signedName} on ${fmt(c.consent.signedAt)}.`
              : "Not yet signed — you can't act on this one."}{" "}
            {view.coverage.asked}/{view.coverage.total} questions put to them.
          </p>
        </div>

        {/* Above the actions, because it changes what the right action is. */}
        {c.escalation && (
          <Panel
            title={c.escalation.urgency === "now" ? "Someone needs a person NOW" : "Someone asked for a person"}
            tone="warn"
          >
            <p style={{ fontSize: 14.5, fontWeight: 600, margin: 0 }}>{c.escalation.reason}</p>
            <p style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 6 }}>
              Raised {fmt(c.escalation.at)}
              {c.fields.phone ? ` · ${c.fields.phone}` : " · no phone number on file yet"}
            </p>
          </Panel>
        )}

        {c.declined && (
          <Panel title="We couldn't take this one" tone="warn">
            <p style={{ fontSize: 14, margin: 0 }}>{c.declined.reason}</p>
            <p style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 6 }}>
              Rule “{c.declined.rule}” · {fmt(c.declined.at)}. The client was told and nothing
              further was collected. Change the rule in this business's config if that was wrong.
            </p>
          </Panel>
        )}

        <Actions view={view} caseId={caseId} token={token} onChanged={refresh} />

        {view.clarifications.length > 0 && (
          <Panel
            title="Questions you've sent"
            tone={view.clarifications.some((q) => q.status === "open") ? "warn" : undefined}
          >
            <ul style={{ ...ul, display: "grid", gap: 10 }}>
              {view.clarifications.map((q) => (
                <li key={q.id} style={{ ...li, listStyle: "none", marginLeft: 0 }}>
                  <div style={{ fontWeight: 600 }}>
                    {q.question}
                    {q.field && (
                      <span style={{ fontWeight: 400, color: "var(--ink-3)" }}>
                        {" "}
                        · {view.pack.fields.find((f) => f.name === q.field)?.label ?? q.field}
                      </span>
                    )}
                  </div>
                  <div style={{ color: "var(--ink-2)", marginTop: 2 }}>
                    {q.status === "answered" ? (
                      <>
                        <span style={{ color: "var(--ok, var(--ink-2))" }}>Answered</span>{" "}
                        {fmt(q.answeredAt!)} — “{q.answer}”
                      </>
                    ) : q.status === "withdrawn" ? (
                      <>Withdrawn</>
                    ) : (
                      <>
                        Sent {fmt(q.askedAt)}
                        {q.askedBy ? ` by ${q.askedBy}` : ""} · no answer yet ·{" "}
                        <button
                          type="button"
                          onClick={() => {
                            fetch(
                              `/api/agent/case/${caseId}/clarification/${q.id}/withdraw?k=${encodeURIComponent(token)}`,
                              { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" },
                            ).then(refresh);
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            font: "inherit",
                            color: "var(--ink-3)",
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          never mind
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Panel>
        )}

        {view.attention.length > 0 && (
          <Panel title="Needs your attention" tone="warn">
            <ul style={ul}>
              {view.attention.map((a, i) => (
                <li key={i} style={li}>{a}</li>
              ))}
            </ul>
          </Panel>
        )}

        {view.checklist.length > 0 && (
          <Panel title="Before you act">
            <ul style={ul}>
              {view.checklist.map((a, i) => (
                <li key={i} style={li}>{a}</li>
              ))}
            </ul>
          </Panel>
        )}

        <Panel title="Client">
          <dl className="grid gap-x-6 gap-y-1.5" style={{ gridTemplateColumns: "auto 1fr", margin: 0 }}>
            {view.pack.fields
              .filter((f) => (c.fields[f.name] ?? "").trim())
              .map((f) => (
                <Row key={f.name} label={f.label} value={c.fields[f.name]!} />
              ))}
            {Object.entries(c.checklists)
              .filter(([, ids]) => ids.length)
              .map(([listId, ids]) => (
                <Row
                  key={listId}
                  label={view.pack.checklists.find((l) => l.id === listId)?.title ?? listId}
                  value={ids.map(optionLabel).join(", ")}
                />
              ))}
          </dl>
        </Panel>

        {buckets.map(([bucket, items]) => (
          <Panel key={bucket} title={bucket}>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div
                  key={i}
                  style={{
                    borderLeft: `2px solid ${it.needsCheck ? "var(--warn)" : "var(--line-2)"}`,
                    paddingLeft: 12,
                  }}
                >
                  <div style={{ fontSize: 14 }}>
                    <b style={{ fontWeight: 600 }}>{it.what}</b>
                    {it.amount ? ` — ${it.amount}` : ""}
                    {it.when && <span style={{ color: "var(--ink-3)" }}> · {it.when}</span>}
                  </div>
                  {it.detail && (
                    <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 2 }}>{it.detail}</div>
                  )}
                  <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                    {optionLabel(it.sourceId)} · {it.hasEvidence ? "receipt held" : "no receipt"}
                    {it.needsCheck && " · client estimate"}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        ))}

        {Object.keys(c.topicNotes).length > 0 && (
          <Panel title="Detail notes">
            <div className="space-y-2">
              {Object.entries(c.topicNotes).map(([k, v]) => (
                <div key={k}>
                  <b style={{ fontWeight: 600, textTransform: "capitalize", fontSize: 13.5 }}>{k}</b>
                  <div style={{ fontSize: 13.5, color: "var(--ink-2)" }}>{v}</div>
                </div>
              ))}
            </div>
          </Panel>
        )}

        {/*
          The reason this page exists rather than an attachment. One value at
          a time, on a deliberate click, and every reveal is written to the
          audit log — so a practice can show who looked at what, and when.
        */}
        <Panel title="Private details" tone="accent">
          <p style={{ fontSize: 12.5, color: "var(--ink-2)", marginBottom: 10 }}>
            Shown one at a time. Every reveal is recorded against this case.
          </p>
          <div className="space-y-2">
            {view.pack.secureGroups.map((g) =>
              g.fields.map((f) => {
                const key = `${g.id}.${f}`;
                const filled = c.secureFilled[key];
                const value = revealed[key];
                return (
                  <div key={key} className="flex items-center gap-3" style={{ fontSize: 13.5 }}>
                    <span style={{ color: "var(--ink-3)", minWidth: 150 }}>{f}</span>
                    {!filled ? (
                      <span style={{ color: "var(--ink-3)" }}>not provided</span>
                    ) : value ? (
                      <code className="mono" style={{ fontSize: 14, letterSpacing: "0.02em" }}>
                        {value}
                      </code>
                    ) : (
                      <button
                        type="button"
                        onClick={() => reveal(g.id, f)}
                        disabled={revealing === key}
                        className="btn btn-quiet"
                        style={{ padding: "0.25rem 0.6rem", fontSize: 12.5 }}
                      >
                        {revealing === key ? "…" : "Reveal"}
                      </button>
                    )}
                  </div>
                );
              }),
            )}
          </div>
        </Panel>

        {/* ── What the modules collected ─────────────────────────────
            Anything an assembled module has to say for itself, in its own
            words. The server has always sent this and nothing rendered
            it, so a module could collect something perfectly and the
            person acting on the case would never see it.

            Availability is what made that obvious: crAIg asks when suits,
            records it, and whoever books the van was none the wiser.

            Deliberately generic — keyed off whatever modules are live, so
            a business switching one on grows a line here without this
            file changing. Documents and the declaration keep their own
            panels below; they were always rendered and they earn the
            space. */}
        {(() => {
          const said = (view.modules ?? []).filter((m: ModuleView) => m.summary || m.done);
          if (!said.length) return null;
          return (
            <Panel title="Collected">
              <dl className="space-y-2" style={{ margin: 0 }}>
                {said.map((m: ModuleView) => (
                  <div key={m.id} className="flex gap-3" style={{ fontSize: 13.5 }}>
                    <dt style={{ color: "var(--ink-3)", minWidth: 130, flexShrink: 0 }}>
                      {LABELS[m.id] ?? m.id}
                    </dt>
                    <dd style={{ margin: 0 }}>
                      {m.summary ?? (m.done ? "done" : "—")}
                    </dd>
                  </div>
                ))}
              </dl>
            </Panel>
          );
        })()}

        <Panel title={`Documents (${c.documents.length})`}>
          {c.documents.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--ink-3)" }}>Nothing uploaded.</p>
          ) : (
            <>
              <p style={{ fontSize: 12.5, color: "var(--ink-2)", marginBottom: 10 }}>
                Unsorted and unread by us — exactly as the client handed them over.
              </p>
              <div className="flex flex-wrap gap-2">
                {c.documents.map((d) => (
                  <a
                    key={d.id}
                    href={`/api/agent/case/${c.id}/document/${d.id}?k=${encodeURIComponent(token)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-quiet"
                    style={{ fontSize: 12.5, fontWeight: 500 }}
                  >
                    {d.mime.startsWith("image/") ? "🖼" : "📕"} {d.name}
                    <span style={{ color: "var(--ink-3)" }}>{Math.round(d.sizeBytes / 1024)}kb</span>
                  </a>
                ))}
              </div>
            </>
          )}
        </Panel>

        {c.consent && (
          <Panel title="Declaration">
            <p style={{ fontSize: 13, lineHeight: 1.5 }}>{c.consent.statement}</p>
            <div className="flex items-end gap-4 mt-3 flex-wrap">
              <img
                src={c.consent.signatureImage}
                alt="Client signature"
                style={{
                  height: 70,
                  background: "#fff",
                  border: "1px solid var(--line)",
                  borderRadius: 8,
                  padding: 4,
                }}
              />
              <div style={{ fontSize: 12.5, color: "var(--ink-2)" }}>
                <div>
                  <b style={{ fontWeight: 600 }}>{c.consent.signedName}</b> · {fmt(c.consent.signedAt)}
                </div>
                <div style={{ color: "var(--ink-3)", marginTop: 2 }}>{c.consent.basis}</div>
              </div>
            </div>
          </Panel>
        )}

        {/* Last, and collapsed. If this is ever the fastest way to understand
            a case, everything above it has failed. */}
        <Panel title="Raw conversation">
          {!showTranscript ? (
            <button type="button" onClick={loadTranscript} className="btn btn-quiet">
              Show the transcript
            </button>
          ) : (
            <div className="space-y-2" style={{ fontSize: 13 }}>
              {transcript.map((m, i) => (
                <div key={i}>
                  <span style={{ color: "var(--ink-3)" }}>
                    {m.role === "client" ? "client" : "crAIg"}:{" "}
                  </span>
                  {m.secureLabel ? <i>({m.secureLabel} entered privately)</i> : m.text}
                </div>
              ))}
            </div>
          )}
        </Panel>

        <p style={{ fontSize: 11.5, color: "var(--ink-3)", textAlign: "center" }}>
          Pack {view.pack.version} · case {c.id.slice(0, 8)} · {view.cost.turns} turns
        </p>
        <LetCraigDoIt />
      </main>
    </div>
  );
}

/* ── Actions ────────────────────────────────────────────────────────────
   Five verbs. Four move the case along; one is the reason the product
   exists — the reviewer flags a single thing, and crAIg goes and asks it in
   the client's own language rather than the reviewer writing an email and
   waiting. */

function Actions({
  view,
  caseId,
  token,
  onChanged,
}: {
  view: AgentView;
  caseId: string;
  token: string;
  onChanged(): void;
}) {
  const [asking, setAsking] = useState(false);
  const [question, setQuestion] = useState("");
  const [field, setField] = useState("");
  const [who, setWho] = useState(() => localStorage.getItem("craig:agent:name") ?? "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);

  const post = async (path: string, body: unknown) => {
    setBusy(true);
    try {
      const res = await fetch(`/api/agent/case/${caseId}/${path}?k=${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...(body as object), who: who || undefined }),
      });
      const out = await res.json();
      if (!res.ok) {
        setSent(out.error);
        return null;
      }
      onChanged();
      return out;
    } finally {
      setBusy(false);
    }
  };

  const status = view.case.status;

  return (
    <section className="card px-5 py-4">
      <div className="mb-3">
        <SectionLabel>What now</SectionLabel>
      </div>

      {status === "awaiting_client" ? (
        <p style={{ fontSize: 13.5, color: "var(--ink-2)" }}>
          Waiting on the client. crAIg has emailed them and will ask when they open it — you'll
          get another email once they've answered.
        </p>
      ) : status === "closed" ? (
        <div className="flex flex-wrap items-center gap-3">
          <p style={{ fontSize: 13.5, color: "var(--ink-2)", margin: 0 }}>
            This one's closed. Nothing further to do.
          </p>
          <button
            type="button"
            className="btn btn-quiet"
            disabled={busy}
            onClick={() => post("action", { action: "reopen" })}
          >
            Reopen
          </button>
        </div>
      ) : !asking ? (
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn btn-primary" onClick={() => setAsking(true)}>
            Ask the client something
          </button>
          <button
            type="button"
            className="btn btn-quiet"
            disabled={busy || status === "accepted"}
            onClick={() => post("action", { action: "accept" })}
          >
            {status === "accepted" ? "Accepted" : "Accept"}
          </button>
          <button
            type="button"
            className="btn btn-quiet"
            disabled={busy || status === "escalated"}
            onClick={() => post("action", { action: "escalate" })}
          >
            {status === "escalated" ? "Escalated" : "Escalate"}
          </button>
          <button
            type="button"
            className="btn btn-quiet"
            disabled={busy}
            onClick={() => post("action", { action: "close" })}
          >
            Close
          </button>
        </div>
      ) : (
        <div className="space-y-2.5">
          <p style={{ fontSize: 13, color: "var(--ink-2)" }}>
            crAIg will ask this in {view.case.language === "en" ? "English" : "their language"} and
            put the answer straight onto the case. They'll get one short email.
          </p>

          <select
            value={field}
            onChange={(e) => setField(e.target.value)}
            className="field"
            aria-label="Which detail is this about"
          >
            <option value="">Not about a specific field</option>
            {view.pack.fields.map((f) => (
              <option key={f.name} value={f.name}>
                {f.label}
              </option>
            ))}
          </select>

          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
            placeholder="e.g. Which union were the fees paid to?"
            className="field"
            style={{ resize: "vertical" }}
          />

          <input
            value={who}
            onChange={(e) => {
              setWho(e.target.value);
              localStorage.setItem("craig:agent:name", e.target.value);
            }}
            placeholder="Your name (for the record)"
            className="field"
          />

          <div className="flex items-center gap-3">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || question.trim().length < 3}
              onClick={async () => {
                const out = await post("ask", { question: question.trim(), field: field || undefined });
                if (out) {
                  setAsking(false);
                  setQuestion("");
                  setField("");
                  setSent(
                    out.notified === "sent"
                      ? "Asked — the client has been emailed."
                      : "Asked. We couldn't email them, so they'll see it next time they open their case.",
                  );
                }
              }}
            >
              {busy ? "Sending…" : "Send the question"}
            </button>
            <button
              type="button"
              onClick={() => setAsking(false)}
              style={{
                fontSize: 13,
                background: "none",
                border: "none",
                color: "var(--ink-3)",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {sent && (
        <p style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 10 }}>{sent}</p>
      )}
    </section>
  );
}

/* ── Furniture ──────────────────────────────────────────────────────────── */

const ul: React.CSSProperties = { margin: 0, paddingLeft: 18 };
const li: React.CSSProperties = { fontSize: 13.5, marginBottom: 4, color: "var(--ink-2)" };

const fmt = (iso: string) =>
  new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });

function Centre({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen flex items-center justify-center px-4">{children}</div>;
}

function Panel({
  title,
  children,
  tone,
}: {
  title: string;
  children: React.ReactNode;
  tone?: "warn" | "accent";
}) {
  return (
    <section
      className="card px-5 py-4"
      style={
        tone === "warn"
          ? { background: "var(--warn-tint)", borderColor: "var(--warn)" }
          : tone === "accent"
            ? { borderColor: "var(--accent-edge)" }
            : undefined
      }
    >
      <div className="mb-2.5" style={{ textTransform: "capitalize" }}>
        <SectionLabel>{title}</SectionLabel>
      </div>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt style={{ fontSize: 13, color: "var(--ink-3)", whiteSpace: "nowrap" }}>{label}</dt>
      <dd style={{ fontSize: 13.5, margin: 0 }}>{value}</dd>
    </>
  );
}

function Pill({ children, tone }: { children: React.ReactNode; tone?: "ok" | "warn" }) {
  return (
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 999,
        background:
          tone === "ok" ? "var(--ok-tint)" : tone === "warn" ? "var(--warn-tint)" : "var(--surface-2)",
        color: tone === "ok" ? "var(--ok)" : tone === "warn" ? "var(--warn)" : "var(--ink-2)",
        border: "1px solid var(--line)",
      }}
    >
      {children}
    </span>
  );
}
